// src/hooks/useAuth.ts
// Firebase auth context, provider, and hook

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import {
  auth,
  onAuthStateChanged,
  signInWithGoogle,
  signOut,
} from '@/lib/firebase'
import type { User } from '@/lib/firebase'

interface AuthContextValue {
  user: User | null
  loading: boolean
  error: string | null
  handleSignIn: () => Promise<void>
  handleSignOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && firebaseUser.email?.endsWith('@globalcomix.com')) {
        setUser(firebaseUser)
      } else {
        setUser(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const handleSignIn = useCallback(async () => {
    setError(null)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
    }
  }, [])

  const handleSignOut = useCallback(async () => {
    setError(null)
    try {
      await signOut()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-out failed')
    }
  }, [])

  const value: AuthContextValue = { user, loading, error, handleSignIn, handleSignOut }

  return (
    <AuthContext value={value}>
      {children}
    </AuthContext>
  )
}
